import React, { useState, useEffect, useCallback } from "react";
import {
  Save,
  RefreshCw,
  AlertCircle,
  Check,
  Plus,
  Trash2,
  Edit,
  Power,
  MessageSquare,
  X,
  Info,
} from "lucide-react";
import {
  getAllButtonTemplates,
  createButtonTemplate,
  updateButtonTemplate,
  deleteButtonTemplate,
  toggleButtonTemplateStatus,
} from "../services/buttonTemplateApi";

/**
 * Button Templates Admin Page
 *
 * Purpose: Manage button templates that appear as suggestion chips in the chatbot.
 *
 * What are Button Templates?
 * - Pre-configured responses that trigger when users click suggestion buttons
 * - Faster than AI processing (~200ms vs ~2000ms)
 * - Perfect for common queries like "Menu", "Coffee", "Stores", etc.
 *
 * How it works:
 * 1. User clicks a suggestion chip (e.g., [Menu])
 * 2. System checks if a template exists for that trigger phrase
 * 3. Returns the pre-configured response with follow-up suggestions
 *
 * Note: Typed messages skip templates and go through AI pipeline for context awareness.
 */
const ButtonTemplatesAdmin = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form state for creating/editing templates
  const [formData, setFormData] = useState({
    key: "",
    label: "",
    response_text: "",
    suggestion_buttons: [{ label: "", action: "", source: "backend", type: "other" }],
    active: true,
    priority: 0,
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  /**
   * Load templates from API
   */
  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);

      const templatesResponse = await getAllButtonTemplates();

      // Handle both nested data structure (response.data.data) and direct data structure (response.data)
      const templatesData = templatesResponse.data?.data || templatesResponse.data || [];
      setTemplates(templatesData);
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("Failed to load data", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        key: template.key,
        label: template.label,
        response_text: template.response_text,
        suggestion_buttons:
          template.suggestion_buttons && template.suggestion_buttons.length > 0
            ? template.suggestion_buttons.map((btn) => ({
                label: btn.label || "",
                action: btn.action || "",
                source: btn.source || "backend",
                type: btn.type || "other",
                interaction_type: btn.interaction_type,
              }))
            : [{ label: "", action: "", source: "backend", type: "other" }],
        active: template.active,
        priority: template.priority,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        key: "",
        label: "",
        response_text: "",
        suggestion_buttons: [{ label: "", action: "", source: "backend", type: "other" }],
        active: true,
        priority: 0,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  /**
   * Save or update a button template
   *
   * This either creates a new template or updates an existing one.
   * All empty fields are filtered out before saving.
   *
   * Note: trigger_phrases are auto-generated from the key field for backward compatibility.
   * Button matching now uses key-based lookup instead of fuzzy phrase matching.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up the data by removing empty suggestion buttons and setting source to backend
      const cleanedData = {
        ...formData,
        // Auto-generate trigger_phrases from key for backward compatibility
        // (Button matching now uses key directly, but DB field still required)
        trigger_phrases: [formData.key, formData.label],
        suggestion_buttons: formData.suggestion_buttons
          .filter((b) => b.label.trim() !== "" && b.action.trim() !== "")
          .map((b) => ({
            label: b.label.trim(),
            action: b.action.trim(),
            source: "backend",
            type: b.type || "other",
            ...(b.interaction_type ? { interaction_type: b.interaction_type } : {}),
          })),
      };

      if (editingTemplate) {
        await updateButtonTemplate(editingTemplate.id, cleanedData);
        showToast("Button template updated successfully!", "success");
      } else {
        await createButtonTemplate(cleanedData);
        showToast("Button template created successfully!", "success");
      }
      handleCloseModal();
      fetchTemplates();
    } catch (error) {
      console.error("Error saving button template:", error);
      showToast(error.response?.data?.message || "Failed to save button template", "error");
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await toggleButtonTemplateStatus(id);
      showToast("Template status updated!", "success");
      fetchTemplates();
    } catch (error) {
      console.error("Error toggling template status:", error);
      showToast("Failed to toggle template status", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteButtonTemplate(id);
      showToast("Template deleted successfully!", "success");
      setDeleteConfirmId(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      showToast("Failed to delete template", "error");
    }
  };

  // Removed: handleAddTriggerPhrase, handleRemoveTriggerPhrase, handleTriggerPhraseChange
  // Trigger phrases are no longer user-editable (auto-generated from key)

  const handleAddSuggestionButton = () => {
    setFormData((prev) => ({
      ...prev,
      suggestion_buttons: [
        ...prev.suggestion_buttons,
        { label: "", action: "", source: "backend", type: "other" },
      ],
    }));
  };

  const handleRemoveSuggestionButton = (index) => {
    setFormData((prev) => ({
      ...prev,
      suggestion_buttons: prev.suggestion_buttons.filter((_, i) => i !== index),
    }));
  };

  const handleSuggestionButtonChange = (index, field, value) => {
    setFormData((prev) => {
      const newButtons = [...prev.suggestion_buttons];
      newButtons[index] = { ...newButtons[index], [field]: value };
      return { ...prev, suggestion_buttons: newButtons };
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#6b4226]" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Button Templates</h2>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors flex items-center gap-2 font-semibold"
        >
          <Plus size={20} />
          Add Template
        </button>
      </div>

      {templates.length === 0 && !isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-gray-400 mb-4">
            <MessageSquare size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Button Templates</h3>
          <p className="text-gray-600 mb-4">Get started by creating your first button template.</p>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors"
          >
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-lg shadow p-5 border border-gray-100"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <MessageSquare className="text-[#6b4226]" size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{template.label}</h3>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="bg-gray-100 px-2 py-0.5 rounded">Key: {template.key}</span>
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          Priority: {template.priority}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded ${
                            template.active
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {template.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Matching:</span>
                      <div className="mt-1">
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                          Button clicks with key: "{template.key}"
                        </span>
                      </div>
                    </div>

                    <div>
                      <span className="font-medium text-gray-700">Response:</span>
                      <p className="text-gray-600 mt-1">{template.response_text}</p>
                    </div>

                    {template.suggestion_buttons && template.suggestion_buttons.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700">Suggestion Buttons:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {template.suggestion_buttons.map((button, idx) => (
                            <span
                              key={idx}
                              className="bg-orange-100 text-[#6b4226] px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2"
                            >
                              <span>{button.label}</span>
                              {button.type && (
                                <span className="bg-white text-[#6b4226] px-2 py-0.5 rounded-full border border-[#e5b48f]">
                                  {button.type}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggleStatus(template.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      template.active
                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                    title={template.active ? "Deactivate" : "Activate"}
                  >
                    <Power size={18} />
                  </button>
                  <button
                    onClick={() => handleOpenModal(template)}
                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                  {deleteConfirmId === template.id ? (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        title="Confirm Delete"
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="p-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(template.id)}
                      className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editingTemplate ? "Edit Button Template" : "Add Button Template"}
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key (Unique Identifier)
                  </label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                    placeholder="e.g., menu, stores, coffee"
                    required
                    disabled={!!editingTemplate}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button Label
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                    placeholder="e.g., Menu"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                    min="0"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Higher priority = checked first</p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
                    />
                    Active
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Text
                </label>
                <textarea
                  value={formData.response_text}
                  onChange={(e) => setFormData({ ...formData, response_text: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                  placeholder="Enter the response message..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suggestion Buttons
                </label>
                <div className="space-y-2">
                  {formData.suggestion_buttons.map((button, index) => (
                    <div key={index} className="flex flex-wrap gap-2">
                      <div className="flex-1 min-w-[180px]">
                        <input
                          type="text"
                          value={button.label}
                          onChange={(e) =>
                            handleSuggestionButtonChange(index, "label", e.target.value)
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                          placeholder="Button Label"
                        />
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <input
                          type="text"
                          value={button.action}
                          onChange={(e) =>
                            handleSuggestionButtonChange(index, "action", e.target.value)
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                          placeholder="Action (what to send)"
                        />
                      </div>
                      <div className="w-32">
                        <select
                          value={button.type || "other"}
                          onChange={(e) => handleSuggestionButtonChange(index, "type", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                        >
                          <option value="food">food</option>
                          <option value="drink">drink</option>
                          <option value="store">store</option>
                          <option value="merchandise">merchandise</option>
                          <option value="other">other</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSuggestionButton(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddSuggestionButton}
                    className="text-sm text-[#6b4226] hover:underline flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Add Suggestion Button
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-5 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors flex items-center gap-2 font-semibold"
                >
                  <Save size={18} />
                  {editingTemplate ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
              toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
            }`}
          >
            {toast.type === "success" ? <Check size={20} /> : <AlertCircle size={20} />}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ButtonTemplatesAdmin;
